/**
 * UzivateleWorkspace — Phase 10.4 user administration.
 *
 * Lives under `/platforma?tab=uzivatele` (see PlatformaPage). Admin-only —
 * a non-admin never sees the Platforma area. The workspace is a
 * Toolband (search + Nový uživatel CTA) + bordered `DataTable`, with a
 * row click opening `UserFormModal` in edit mode.
 *
 * Greenfield UI — the legacy app had no user-administration surface, so
 * patterns are extrapolated from `CenikyWorkspace.tsx`.
 */

import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/Button';
import { Chip } from '@/components/Chip';
import { ColorSwatch } from '@/components/ColorSwatch';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Marker } from '@/components/Marker';
import { SearchInput } from '@/components/SearchInput';
import { Skeleton } from '@/components/Skeleton';
import { Toolband } from '@/components/Toolband';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { useUsersListQuery, type MgmtUser } from './api';
import { UserFormModal } from './UserFormModal';

interface UzivateleWorkspaceProps {
  isAdmin: boolean;
}

export function UzivateleWorkspace({ isAdmin }: UzivateleWorkspaceProps) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [stavFilter, setStavFilter] = useState<'vse' | 'aktivni' | 'neaktivni'>('vse');
  const [modal, setModal] = useState<{ mode: 'create' } | { mode: 'edit'; user: MgmtUser } | null>(
    null,
  );

  // Growable page — 200 silently truncated at multi-dealership scale.
  const [listLimit, setListLimit] = useState(200);
  useEffect(() => setListLimit(200), [debouncedSearch, stavFilter]);
  const list = useUsersListQuery(
    {
      search: debouncedSearch.trim() || undefined,
      aktivni: stavFilter === 'vse' ? undefined : stavFilter === 'aktivni' ? 1 : 0,
      limit: listLimit,
    },
    isAdmin,
  );
  const users = list.data?.users ?? [];
  const listTotal = list.data?.total ?? users.length;

  const cols = useMemo<DataTableColumn<MgmtUser>[]>(
    () => [
      {
        id: 'nick',
        priority: 'primary',
        header: 'Nick',
        cell: (u) => (
          <span className="font-mono text-[12px] tracking-[-0.01em] text-ink">{u.nick}</span>
        ),
        width: '140px',
      },
      {
        id: 'jmeno',
        priority: 'secondary',
        header: 'Jméno',
        cell: (u) => (
          <span className="truncate text-ink">
            {[u.jmeno, u.prijmeni].filter(Boolean).join(' ') || '—'}
          </span>
        ),
        width: 'minmax(0, 1fr)',
      },
      {
        id: 'email',
        priority: 'secondary',
        header: 'E-mail',
        cell: (u) => (
          <span className="truncate font-mono text-[12px] text-ink-2">{u.email || '—'}</span>
        ),
        width: 'minmax(0, 1.2fr)',
      },
      {
        id: 'stav',
        priority: 'meta',
        header: 'Stav',
        cell: (u) =>
          u.aktivni === 0 ? (
            <Marker tone="danger">Neaktivní</Marker>
          ) : (
            <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
              Aktivní
            </span>
          ),
        width: '110px',
      },
      {
        id: 'admin',
        priority: 'meta',
        header: 'Role',
        cell: (u) =>
          u.admin === 1 ? (
            <Marker tone="accent">Admin</Marker>
          ) : (
            <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
              Uživatel
            </span>
          ),
        width: '110px',
      },
      {
        id: 'barva',
        priority: 'meta',
        header: 'Barva',
        cell: (u) => <ColorSwatch color={u.barva || '#dee0e6'} />,
        width: '60px',
        align: 'center',
      },
      {
        id: 'action',
        header: '',
        cell: () => <Icon name="edit" size={14} className="text-ink-3 group-hover:text-ink" />,
        width: '40px',
        align: 'center',
      },
    ],
    [],
  );

  if (!isAdmin) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-8 pb-8 pt-5">
          <EmptyState
            eyebrow="403"
            title="Správa uživatelů je dostupná jen administrátorům."
            description="Pro přístup kontaktuj správce firmy."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <Toolband>
        <SearchInput
          wrapperClassName="min-w-[280px]"
          kbd={null}
          placeholder="Hledat nick, jméno, e-mail…"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />

        <Chip active={stavFilter === 'vse'} onClick={() => setStavFilter('vse')}>
          Vše
        </Chip>
        <Chip active={stavFilter === 'aktivni'} onClick={() => setStavFilter('aktivni')}>
          Aktivní
        </Chip>
        <Chip active={stavFilter === 'neaktivni'} onClick={() => setStavFilter('neaktivni')}>
          Neaktivní
        </Chip>

        <Toolband.Spacer />

        <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          {list.isLoading ? '—' : `${users.length} uživatelů`}
        </span>
        <Button
          variant="primary"
          size="sm"
          icon={<Icon name="plus" size={12} />}
          onClick={() => setModal({ mode: 'create' })}
        >
          Nový uživatel
        </Button>
      </Toolband>

      <div
        data-testid="management-uzivatele"
        className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-8 pb-8 pt-5"
      >
        {list.isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : users.length === 0 ? (
          <EmptyState
            eyebrow="Prázdné"
            title={
              debouncedSearch.trim()
                ? 'Žádný uživatel neodpovídá hledání.'
                : 'Zatím žádní uživatelé.'
            }
            description={
              debouncedSearch.trim()
                ? 'Zkuste jiný výraz nebo vyčistěte vyhledávání.'
                : 'Vytvořte prvního uživatele tlačítkem „Nový uživatel“ vpravo nahoře.'
            }
          />
        ) : (
          <DataTable<MgmtUser>
            aria-label="Uživatelé"
            variant="bordered"
            columns={cols}
            data={users}
            getRowId={(u) => String(u.id_uzivatele)}
            onRowClick={(u) => setModal({ mode: 'edit', user: u })}
          />
        )}
        {listTotal > users.length && (
          <div className="flex items-center justify-center gap-3 py-3 font-mono text-[11px] text-ink-3">
            <span>
              Zobrazeno {users.length} z {listTotal} uživatelů
            </span>
            <Button size="sm" variant="secondary" onClick={() => setListLimit((l) => l + 200)}>
              Načíst další
            </Button>
          </div>
        )}
      </div>

      <UserFormModal
        open={modal != null}
        mode={modal?.mode ?? 'create'}
        user={modal?.mode === 'edit' ? modal.user : undefined}
        onOpenChange={(open) => {
          if (!open) setModal(null);
        }}
      />
    </div>
  );
}
